import { useEffect, useState, useMemo } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    IconButton,
    Paper
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import LoadingButton from '@mui/lab/LoadingButton';
import { postRequest } from 'src/services';
import { useSnackbar } from 'src/components/snackbar';
import FormProvider, { RHFSelect } from 'src/components/hook-form';
import { endpoints } from 'src/utils/axios';
import Iconify from 'src/components/iconify';
import { handleErrorMessages } from 'src/pages/components/errorhanling-formating';
import { useParams } from 'src/routes/hooks';

export const PlanAddonCreateModal = ({ open, onClose, reload, currentItem, features }) => {
    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();
    const [ loading, setLoading ] = useState(false);
    const [ checkedItems, setCheckedItems ] = useState([]);
    const [ selectedModuleId, setSelectedModuleId ] = useState('');
    const { id } = useParams();

    // Yup Schema
    const NewDocumentSchema = Yup.object().shape({
        product_module_id: Yup.string().required('Module is required'),
    });

    // Default values
    const defaultValues = useMemo(() => ({
        product_module_id: '',
    }), [ currentItem ]);

    const methods = useForm({
        resolver: yupResolver(NewDocumentSchema),
        defaultValues,
    });

    const {
        reset,
        watch,
        setValue,
        handleSubmit,
        formState: { isSubmitting },
    } = methods;

    const selectedModule = features?.find(item => item.id.toString() === watch('product_module_id'));



    const addonFeatures = useMemo(() => {
        return selectedModule?.feature_list?.filter((f) => f.is_addon === 1) || [];
    }, [ selectedModule ]);
    useEffect(() => {
        if (addonFeatures.length > 0) {
            setCheckedItems(Array(addonFeatures.length).fill(false));
        } else {
            setCheckedItems([]);
        }
    }, [ addonFeatures ]);


    const onSubmit = handleSubmit(async (data) => {
        setLoading(true);
        try {
            const formData = new FormData();

            formData.append('product_module_id', data.product_module_id);

            const selectedFeatures = selectedModule?.feature_list
                ?.filter((_, index) => checkedItems[ index ])
                ?.map((feature) => feature.id);

            selectedFeatures?.forEach((id, index) => {
                formData.append(`product_features[${index}]`, id);
            });

            const response = await postRequest(
                currentItem?.id
                    ? `${endpoints.admin_dashboard.packages_plans.plans.create_addons}/${currentItem.id}?_method=PUT`
                    : `${endpoints.admin_dashboard.packages_plans.plans.create_addons}/${id}/addons`,
                formData,
                true
            );

            if (response.metadata?.success) {
                reset();
                onClose();
                enqueueSnackbar({
                    variant: 'success',
                    message: response.metadata.message,
                });
            } else {
                handleErrorMessages(response?.metadata?.message || 'An error occurred.');
            }
        } catch (err) {
            console.error('Error:', err);
            handleErrorMessages(err?.message || 'Submission failed.');
        } finally {
            setLoading(false);
        }
    });

    const handleFeatureCheck = (index) => (event) => {
        const updatedChecks = [ ...checkedItems ];
        updatedChecks[ index ] = event.target.checked;
        setCheckedItems(updatedChecks);
    };

    return (
        <Dialog
            open={ open }
            onClose={ (event, reason) => {
                if (reason !== 'backdropClick') {
                    onClose();
                }
            } }
            fullWidth
            maxWidth="md"
            sx={ {
                borderRadius: 'none',
                padding: '60px',
                fontSize: '13px',
                fontFamily: 'arial',
            } }
            transitionDuration={ {
                enter: theme.transitions.duration.shortest,
                exit: theme.transitions.duration.shortest - 80,
            } }
        >
            <FormProvider methods={ methods } onSubmit={ onSubmit }>
                <DialogTitle
                    sx={ { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } }
                >
                    <span
                        className="cardColor"
                        style={ {
                            padding: '7px',
                            marginLeft: '-25px',
                            fontWeight: '700',
                            backgroundColor: '#F4F6F8',
                            color: '#637381',
                        } }
                    >
                        &nbsp;&nbsp; Add new Module Add-on &nbsp;&nbsp;&nbsp;&nbsp;
                    </span>
                    <IconButton onClick={ onClose }>
                        <Iconify icon="mdi:close" />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box mt={ 1 } display="grid" gap={ 2 }>
                        <RHFSelect
                            native
                            name="product_module_id"
                            label="Modules"
                            InputLabelProps={ { shrink: true } }
                            onChange={ (e) => {
                                setValue('product_module_id', e.target.value);
                                setSelectedModuleId(e.target.value);
                            } }
                        >
                            <option value="">Select Module</option>
                            { features?.map((item) => (
                                <option key={ item.id } value={ item.id }>
                                    { item.module_name }
                                </option>
                            )) }
                        </RHFSelect>
                    </Box>

                    { selectedModule?.feature_list?.length > 0 && (
                        <Box mt={ 3 }>
                            <Paper elevation={ 0 } sx={ { backgroundColor: '#f4f7fa', p: 2 } }>
                                <Grid container spacing={ 2 }>
                                    { selectedModule?.feature_list.map((feature, index) => (
                                        <Grid item xs={ 12 } sm={ 6 } md={ 3 } key={ feature.id }>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={ checkedItems[ index ] || false }
                                                        onChange={ handleFeatureCheck(index) }
                                                    />
                                                }
                                                label={ feature.feature_name }
                                            />
                                        </Grid>
                                    )) }

                                </Grid>
                            </Paper>
                        </Box>
                    ) }
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" color="inherit" onClick={ onClose }>
                        Cancel
                    </Button>
                    <LoadingButton
                        type="submit"
                        variant="contained"
                        className="dark-button"
                        loading={ isSubmitting || loading }
                    >
                        { currentItem?.id ? 'Update' : 'Create' }
                    </LoadingButton>
                </DialogActions>
            </FormProvider>
        </Dialog>
    );
};
